/**
 * RootErrorBoundary (web, Issue #519 Phase A)
 *
 * `<F7App>` 내부 최상위에서 children (Panel / AuthRouterSync / AuthGate / PaywallHost)
 * 의 unhandled render error 를 catch 하여 fallback UI 를 표시. AC1 (Error Boundary).
 *
 * Phase A 스코프 — boundary 안전망 도입만. AC2 (race 근본 원인 수정) 은 Phase B 별도 이슈.
 *
 * 정책:
 *   - "새로고침" → window.location.reload() (state clear 시 무한 루프 회피)
 *   - "로그아웃" → supabase.auth.signOut({scope:'local'}) → location.assign('/login')
 *   - DEV: stack + message 표시 / Prod: sanitized 메시지만 (보안 leak 방지)
 *   - console.error 는 import.meta.env.DEV 가드
 */
import { Component, type ReactNode } from "react";
import { supabase } from "../lib/supabase";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

const FALLBACK_MESSAGE = "화면을 불러오는 중 문제가 발생했습니다.";

export class RootErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }): void {
    if (import.meta.env.DEV) {
      console.error("[RootErrorBoundary] caught:", error, info.componentStack);
    }
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleLogout = async (): Promise<void> => {
    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error("[RootErrorBoundary] signOut failed:", err);
      }
    }
    window.location.assign("/login");
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }

    const isDev = import.meta.env.DEV;

    return (
      <div
        role="alert"
        aria-live="assertive"
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          gap: "16px",
          textAlign: "center",
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        }}
      >
        <h1 style={{ margin: 0, fontSize: "20px" }}>{FALLBACK_MESSAGE}</h1>
        <p style={{ margin: 0, color: "#666" }}>
          새로고침 또는 로그아웃 후 다시 시도해주세요.
        </p>

        {isDev && (
          <pre
            data-testid="error-boundary-dev-detail"
            style={{
              maxWidth: "90vw",
              maxHeight: "30vh",
              overflow: "auto",
              padding: "12px",
              background: "#f5f5f5",
              border: "1px solid #ddd",
              borderRadius: "4px",
              fontSize: "12px",
              textAlign: "left",
              whiteSpace: "pre-wrap",
            }}
          >
            {error.message}
            {error.stack ? `\n\n${error.stack}` : ""}
          </pre>
        )}

        <div style={{ display: "flex", gap: "12px" }}>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              border: "1px solid #007aff",
              background: "#007aff",
              color: "#fff",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            새로고침
          </button>
          <button
            type="button"
            onClick={this.handleLogout}
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              border: "1px solid #ccc",
              background: "#fff",
              color: "#333",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            로그아웃
          </button>
        </div>
      </div>
    );
  }
}
